import { Box, Divider, Paper, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNotification } from "../../../../componentes/notificationProvider";
import NewAlertModal from "../../../../componentes/newAlertModal";
import FlowHeader from "../components/flowHeader";
import FlowTable from "../components/flowTable";
import { useTabStyles } from "../components/styles";
import ModalRouter from "./modalRouter";
import MassRouters from "./massRouters";
import { routerColumns } from "./columns";

import { Router, RouterPayload } from "./types";
import api from "../../../../services/api";

export default function Routers() {
  const [search, setSearch] = useState<string>("");
  const [routers, setRouters] = useState<Router[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isMassMode, setIsMassMode] = useState<boolean>(false);
  const [tableKey, setTableKey] = useState<number>(0);

  const [openRouterModal, setOpenRouterModal] = useState<boolean>(false);
  const [editingRouter, setEditingRouter] = useState<Router | null>(null);
  const [savingRouter, setSavingRouter] = useState<boolean>(false);

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [selectedRouter, setSelectedRouter] = useState<Router | null>(null);

  const theme = useTheme();
  const styles = useTabStyles(theme);
  const notify = useNotification();

  useEffect(() => {
    getRouters();
  }, []);

  const getRouters = async () => {
    try {
      setLoading(true);

      const response = await api.get("/easyFlow2.0/routers");

      setRouters(Array.isArray(response.data?.data) ? response.data.data : []);
      setTableKey((key) => key + 1);
    } catch (error: any) {
      console.error(error);
      notify(
        `Error:${
          error?.message || "Error: Não foi possível se conectar com o servidor"
        } `,
        "error",
        6000,
      );
    } finally {
      setLoading(false);
    }
  };

  const rowsForDisplay = useMemo(() => {
    if (!search.trim()) return routers;

    const searchLower = search.toLowerCase();
    return routers.filter((router) =>
      Object.values(router).some((value) =>
        String(value ?? "")
          .toLowerCase()
          .includes(searchLower),
      ),
    );
  }, [routers, search]);

  const handleOpenCreate = () => {
    setEditingRouter(null);
    setOpenRouterModal(true);
  };

  const handleEditRow = (router: Router) => {
    setEditingRouter(router);
    setOpenRouterModal(true);
  };

  const handleSaveRouter = async (
    payload: RouterPayload,
    id?: Router["id"],
  ) => {
    try {
      setSavingRouter(true);

      if (id) {
        await api.put(`/easyFlow2.0/routers/${id}`, {
          ...payload,
        });
        notify("Roteador atualizado com sucesso", "success");
      } else {
        await api.post(`/easyFlow2.0/routers`, {
          ...payload,
        });
        notify("Roteador criado com sucesso", "success");
      }

      setOpenRouterModal(false);
      setEditingRouter(null);
      await getRouters();
    } catch (error: any) {
      console.error(error);
      notify(
        error?.response?.data?.error ||
          error?.message ||
          "Não foi possível salvar o roteador",
        "error",
      );
    } finally {
      setSavingRouter(false);
    }
  };

  const handleDeleteRow = (router: Router) => {
    setSelectedRouter(router);
    setOpenDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedRouter) return;
    try {
      await api.delete(`/easyFlow2.0/routers/${selectedRouter.id}`);
      notify("Roteador removido com sucesso!", "success", 3000);
      await getRouters();
    } catch (error: any) {
      console.error(error);
      notify(
        error?.response?.data?.error ||
          error?.message ||
          "Erro ao remover roteador",
        "error",
        3000,
      );
    } finally {
      setOpenDeleteModal(false);
      setSelectedRouter(null);
    }
  };

  return (
    <Paper elevation={0} sx={styles.paper}>
      {isMassMode ? (
        <MassRouters
          routers={routers}
          search={search}
          onSearchChange={setSearch}
          onBack={() => setIsMassMode(false)}
          onSaved={getRouters}
        />
      ) : (
        <>
          <FlowHeader
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Pesquisar roteadores"
            onEnterEditMode={() => setIsMassMode(true)}
            disableEditMode={loading}
            onAdd={handleOpenCreate}
            onRefresh={getRouters}
            loading={loading}
          />
          <Divider
            flexItem
            sx={{ my: 1, borderColor: theme.palette.divider }}
          />
          <Box sx={styles.boxTable}>
            <FlowTable<Router>
              key={`routers-${tableKey}`}
              id="routersFlow"
              rows={rowsForDisplay}
              columns={routerColumns}
              loadingTable={loading}
              onEditRow={handleEditRow}
              onDeleteRow={handleDeleteRow}
            />
          </Box>
        </>
      )}

      <ModalRouter
        open={openRouterModal}
        loading={savingRouter}
        routerData={editingRouter}
        onClose={() => {
          setOpenRouterModal(false);
          setEditingRouter(null);
        }}
        onSave={handleSaveRouter}
      />

      <NewAlertModal
        openModal={openDeleteModal}
        setOpenModal={setOpenDeleteModal}
        textContent={`Tem certeza que deseja excluir o roteador ${
          selectedRouter?.name ?? ""
        }?`}
        handleButton={handleConfirmDelete}
        handleCancel={() => setSelectedRouter(null)}
        severity={"error"}
        singleButton={false}
      />
    </Paper>
  );
}
